export type FaqVariableTokenMap = Map<string, string>;

export type FaqLike = {
   question?: string;
   answer?: string;
};

const UI_VARIABLE_REGEX = /@\[([^\]]+)\]\(([^)]+)\)/g;
const DOUBLE_BRACE_VARIABLE_REGEX = /\{\{([^}]+)\}\}/g;
const SINGLE_BRACE_VARIABLE_REGEX = /\{([^{}]+)\}/g;
const SINGLE_BRACE_STRIP_REGEX = /(?<!\{)\{([^{}]+)\}(?!\})/g;
const FAQ_VAR_PLACEHOLDER_REGEX = /__GAIL_VAR_\d+__/g;

function nextPlaceholder(index: number): string {
   return `__GAIL_VAR_${index}__`;
}

function scrubTokensInText(
   text: string,
   tokens: FaqVariableTokenMap,
   startIndex: number
): { text: string; nextIndex: number } {
   let result = text;
   let index = startIndex;

   const replaceMatches = (regex: RegExp, input: string): string => {
      return input.replace(regex, (match) => {
         const placeholder = nextPlaceholder(index);
         tokens.set(placeholder, match);
         index += 1;
         return placeholder;
      });
   };

   result = replaceMatches(DOUBLE_BRACE_VARIABLE_REGEX, result);
   result = replaceMatches(UI_VARIABLE_REGEX, result);
   result = replaceMatches(SINGLE_BRACE_VARIABLE_REGEX, result);

   return { text: result, nextIndex: index };
}

export function scrubFaqText(
   text: string | undefined,
   existingTokens?: FaqVariableTokenMap,
   startIndex = 0
): { text: string; tokens: FaqVariableTokenMap; nextIndex: number } {
   const tokens: FaqVariableTokenMap = existingTokens ?? new Map();

   if (text === undefined || text === "") {
      return { text: text ?? "", tokens, nextIndex: startIndex };
   }

   const scrubbed = scrubTokensInText(text, tokens, startIndex);
   return { text: scrubbed.text, tokens, nextIndex: scrubbed.nextIndex };
}

export function restoreFaqText(text: string | undefined, tokens: FaqVariableTokenMap): string {
   if (text === undefined || text === "") {
      return text ?? "";
   }

   let result = text;
   for (const [placeholder, original] of tokens) {
      result = result.split(placeholder).join(original);
   }

   return result;
}

function allowedOriginalsFromTokens(tokens: FaqVariableTokenMap): Set<string> {
   return new Set(tokens.values());
}

/**
 * Permanently removes FAQ variable tokens (`@[label](id)`, `{id}`, `{{id}}`, and
 * review placeholders). Known originals from a scrub token map can be preserved
 * during review restore.
 */
export function stripFaqVariableTokens(
   text: string | undefined,
   allowedOriginals?: Set<string>
): string {
   if (text === undefined || text === "") {
      return text ?? "";
   }

   const preserve = allowedOriginals ?? new Set<string>();

   const stripMatches = (regex: RegExp, input: string): string =>
      input.replace(regex, (match) => (preserve.has(match) ? match : ""));

   let result = text;
   result = stripMatches(FAQ_VAR_PLACEHOLDER_REGEX, result);
   result = stripMatches(DOUBLE_BRACE_VARIABLE_REGEX, result);
   result = stripMatches(UI_VARIABLE_REGEX, result);
   result = stripMatches(SINGLE_BRACE_STRIP_REGEX, result);

   return result;
}

export function scrubFaqs(faqs: FaqLike[] | undefined): {
   faqs: FaqLike[];
   tokens: FaqVariableTokenMap;
} {
   if (!Array.isArray(faqs)) {
      return { faqs: [], tokens: new Map() };
   }

   const tokens: FaqVariableTokenMap = new Map();
   let index = 0;

   const scrubbedFaqs = faqs.map((faq) => {
      const questionScrub = scrubFaqText(faq.question, tokens, index);
      index = questionScrub.nextIndex;
      const answerScrub = scrubFaqText(faq.answer, tokens, index);
      index = answerScrub.nextIndex;

      return {
         ...faq,
         question: questionScrub.text,
         answer: answerScrub.text,
      };
   });

   return { faqs: scrubbedFaqs, tokens };
}

export function restoreAndStripFaqs(
   faqs: FaqLike[] | undefined,
   tokens: FaqVariableTokenMap
): FaqLike[] {
   if (!Array.isArray(faqs)) {
      return [];
   }

   const allowedOriginals = allowedOriginalsFromTokens(tokens);

   return faqs.map((faq) => ({
      ...faq,
      question: stripFaqVariableTokens(restoreFaqText(faq.question, tokens), allowedOriginals),
      answer: stripFaqVariableTokens(restoreFaqText(faq.answer, tokens), allowedOriginals),
   }));
}

export function sanitizeFaqsForStorage<T extends FaqLike>(faqs: T[] | undefined): T[] {
   if (!Array.isArray(faqs)) {
      return [];
   }

   return faqs.map((faq) => ({
      ...faq,
      question: stripFaqVariableTokens(faq.question),
      answer: stripFaqVariableTokens(faq.answer),
   }));
}

export function prepareReviewRequestData<T extends Record<string, unknown>>(data: T): {
   data: T;
   faqVarTokens: FaqVariableTokenMap;
} {
   if (!data || typeof data !== "object" || !("faqs" in data)) {
      return { data, faqVarTokens: new Map() };
   }

   const faqs = (data as { faqs?: FaqLike[] }).faqs;
   if (!Array.isArray(faqs)) {
      return { data, faqVarTokens: new Map() };
   }

   const { faqs: scrubbedFaqs, tokens } = scrubFaqs(faqs);

   return {
      data: { ...data, faqs: scrubbedFaqs },
      faqVarTokens: tokens,
   };
}

export function restoreReviewResponseFaqs<T extends Record<string, unknown>>(
   data: T,
   faqVarTokens: FaqVariableTokenMap
): T {
   if (!data || typeof data !== "object" || !("faqs" in data) || faqVarTokens.size === 0) {
      return data;
   }

   const faqs = (data as { faqs?: FaqLike[] }).faqs;
   if (!Array.isArray(faqs)) {
      return data;
   }

   return {
      ...data,
      faqs: restoreAndStripFaqs(faqs, faqVarTokens),
   };
}

/**
 * Strips variable tokens from review response FAQs when no scrub token map exists.
 */
export function sanitizeReviewResponseFaqs<T extends Record<string, unknown>>(data: T): T {
   if (!data || typeof data !== "object" || !("faqs" in data)) {
      return data;
   }

   const faqs = (data as { faqs?: FaqLike[] }).faqs;
   if (!Array.isArray(faqs)) {
      return data;
   }

   return {
      ...data,
      faqs: sanitizeFaqsForStorage(faqs),
   };
}
